import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketplaceBrainDev110Api, type Dev110Status, type SprintDashboard, type SprintValidation } from '../../api/marketplace-brain-dev110';

type Tab = 'overview' | 'modules' | 'providers' | 'jobs' | 'gates' | 'status';

export function MarketplaceOperatingBrainDev110Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Dev110Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-indigo-200 bg-white dark:border-indigo-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-indigo-700 dark:text-indigo-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, prov, jb, gt, st] = await Promise.all([
        marketplaceBrainDev110Api.dashboard(), marketplaceBrainDev110Api.intelligenceModules(),
        marketplaceBrainDev110Api.providers(), marketplaceBrainDev110Api.backgroundJobs(),
        marketplaceBrainDev110Api.qualityGates(), marketplaceBrainDev110Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setProviders(prov); setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await marketplaceBrainDev110Api.validate({
        recommendations_generated: true, priorities_ranked: true, evidence_attached: true,
        reasoning_explainable: true, executive_reports_operational: true,
        marketplace_dna_consumed: true, knowledge_graph_integrated: true,
        learning_updates_operational: true, all_apis_documented: true,
        tests_pass: true, ci_passes: true, performance_budgets_achieved: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Marketplace Operating Brain passes all quality gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Modules' }, { id: 'providers', label: 'Providers' },
    { id: 'jobs', label: 'Jobs' }, { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-indigo-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>DEV-000110 · P0 · Central Intelligence Layer</p>
        <h1 className="text-2xl font-semibold">Marketplace Operating Brain</h1>
        <p className={`text-sm ${muted}`}>Observe → Understand → Prioritize → Recommend → Explain → Learn</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/profit-intelligence-dev109" className={`${accent} hover:underline`}>DEV-000109</Link>
          <span className={muted}>→</span>
          <Link to="/marketplace-brain" className={`${accent} hover:underline`}>Live Executive Workspace</Link>
          <Link to="/platform/marketplace-operating-brain-prd" className={`${accent} hover:underline`}>PRD-000009</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-indigo-300 bg-indigo-50 p-3 text-sm text-indigo-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-indigo-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Not Q&A — Continuous Business Outcome Optimization</h2>
            <p className={`mt-2 text-sm ${muted}`}>The central intelligence layer orchestrating DNA, Listing, Advertising, FBA, Profit and Experiment engines into prioritized, evidence-based decisions.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            {[{ label: 'Objectives', value: dashboard.objectives }, { label: 'Modules', value: dashboard.intelligence_modules },
              { label: 'Providers', value: dashboard.provider_interfaces }, { label: 'Quality Gates', value: dashboard.quality_gates }].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>{m.label}</p><p className="text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={validate} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50">Validate Quality Gates</button>
          {validation && <p className={`text-sm ${validation.valid ? 'text-indigo-600' : 'text-red-600'}`}>{validation.valid ? 'All gates passed' : validation.gaps?.join(', ')}</p>}
        </div>
      )}

      {tab === 'modules' && <div className="grid gap-2 sm:grid-cols-3">{modules.map((m) => (
        <div key={m.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{m.name}</p><p className={`text-xs ${muted}`}>{m.category}</p></div>
      ))}</div>}

      {tab === 'providers' && <div className="grid gap-2 sm:grid-cols-2">{providers.map((p) => (
        <div key={p.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{p.name}</p><p className={`text-xs font-mono ${muted}`}>{p.interface}</p></div>
      ))}</div>}

      {tab === 'jobs' && <div className="space-y-2">{jobs.map((j) => (
        <div key={j.key} className={`flex justify-between rounded-xl border p-3 ${card}`}><span>{j.name}</span><span className={`text-xs ${muted}`}>{j.schedule}</span></div>
      ))}</div>}

      {tab === 'gates' && <div className="space-y-2">{gates.map((g) => (
        <div key={g.key} className={`flex justify-between rounded-xl border p-3 ${card}`}><span>{g.name}</span>{g.blocking && <span className="text-xs text-indigo-600">blocking</span>}</div>
      ))}</div>}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p>Sprint: {status.sprint_id} · {status.status}</p>
          <p className={`text-sm ${muted}`}>DNA integrated: {String(status.dna_integrated)} · Knowledge graph: {String(status.knowledge_graph_integrated)}</p>
        </div>
      )}
    </div>
  );
}
